import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { AccordionModule } from 'primeng/accordion';
import { MultiFormAccordionProps } from './multiform-accordion.types';
import { ButtonModule } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { LeafNode, FormNode, MultiFormAccordionService, BranchNode } from './multi-form-accordion.service';
import { BaseStoreRepository, ElfBaseStore } from '../../store/base-store.repository';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { combineLatest, map, Observable, of } from 'rxjs';

@Component({
  selector: 'sm-multi-form-accordion',
  standalone: true,
  imports: [AccordionModule, CommonModule, ButtonModule, Tooltip, TranslateModule, ConfirmDialogModule],
  templateUrl: './multi-form-accordion.component.html',
  styleUrls: ['./multi-form-accordion.component.scss'],
  providers: [ConfirmationService],
})
export class MultiFormAccordionComponent implements OnInit {
  multiFormAccordionService = inject(MultiFormAccordionService);
  private confirmationService = inject(ConfirmationService);
  private translate = inject(TranslateService);

  @Input() accordionObject?: MultiFormAccordionProps;
  @Input() formFactory?: () => BaseStoreRepository<any>;
  @Input() parentIndexes: number[] = [];

  isTabDisabled(i: number): Observable<boolean> {
    const dl = this.accordionObject?.disabledLevels;
    if (!dl) return of(false);
    if (dl instanceof Observable) {
      return dl.pipe(map(indices => Array.isArray(indices) && indices.includes(i)));
    }
    return of(Array.isArray(dl) && dl.includes(i));
  }

  ngOnInit(): void {
    if (this.accordionObject?.customFactoryMultiFormAccordion) {
      this.multiFormAccordionService = this.accordionObject.customFactoryMultiFormAccordion;
    }
  }

  componentInputs(i: number): Record<string, any> {
    const inputs: Record<string, any> = {
      store: this.getNodeStore(i),
    };

    const combinedIsDisabled = combineLatest([this.accordionObject?.isDisabled || of(false), this.isTabDisabled(i)]).pipe(
      map(([d1, d2]) => d1 || d2)
    );

    this.accordionObject?.injectKeys?.forEach(key => {
      if (key === 'isDisabled') {
        inputs[key] = combinedIsDisabled;
      } else {
        inputs[key as keyof MultiFormAccordionProps] = this.accordionObject?.[key as keyof MultiFormAccordionProps];
      }
    });
    return inputs;
  }

  getChildAccordionObject(i: number): MultiFormAccordionProps | undefined {
    if (!this.accordionObject) return undefined;
    return {
      ...this.accordionObject,
      titles: this.accordionObject.titles?.slice(1),
      isDisabled: combineLatest([this.accordionObject?.isDisabled || of(false), this.isTabDisabled(i)]).pipe(map(([d1, d2]) => d1 || d2)),
      disabledLevels: undefined,
    };
  }

  add(levels: number[]): void {
    const node = this.multiFormAccordionService.getNodeAt(levels);
    if (this.multiFormAccordionService.isBranch(node)) {
      this.multiFormAccordionService.add(levels);
      this.resetAndRender();
    } else {
      console.log('Cannot add to a leaf node at', levels);
    }
  }

  confirmRemove(event: Event, levels: number[]): void {
    event.stopPropagation();
    if (this.getChildren().length == 0) return;

    console.log('confirm remove', this.accordionObject?.titles?.[levels.length - 1]);
    this.confirmationService.confirm({
      message: this.translate.instant('confirm_delete.mfa', {
        entity: this.translate.instant(this.accordionObject?.titles?.[levels.length - 1] as string),
      }),
      header: this.translate.instant('confirmation'),
      acceptLabel: this.translate.instant('common.dialog.confirm'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectLabel: this.translate.instant('common.dialog.cancel'),
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.remove(levels);
      },
      reject: () => {
        // Handle rejection if needed
      },
    });
  }

  remove(levels: number[]): void {
    this.multiFormAccordionService.remove(levels);
    this.resetAndRender();
  }

  getNextLevel(i: number): number[] {
    return [...this.parentIndexes, i];
  }

  isLeaf(i: number): boolean {
    const node = this.multiFormAccordionService.getNodeAt([...this.parentIndexes, i]);
    return this.multiFormAccordionService.isLeaf(node);
  }

  getChildren(): FormNode[] {
    const node = this.multiFormAccordionService.getNodeAt(this.parentIndexes);
    if (this.multiFormAccordionService.isBranch(node)) {
      return node.children;
    }
    return [];
  }

  getNodeStore(i: number) {
    const node = this.multiFormAccordionService.getNodeAt(this.parentIndexes);
    return ((node as BranchNode).children?.[i] as LeafNode)?.store;
  }

  getAllStores(): ElfBaseStore<any>[] {
    return this.multiFormAccordionService.getAllStores();
  }

  // debug function to show all data in the store at each level
  logAllStores() {
    const allStores = this.getAllStores();
    console.log(
      'All stores:',
      allStores.map(store => store)
    );
  }

  resetAndRender(): void {
    const node = this.multiFormAccordionService.getNodeAt(this.parentIndexes);
    if (this.multiFormAccordionService.isBranch(node)) {
      node.children = [...node.children];
    }
  }
}
